import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { logEvent } from '~/config/firebase.config';
import { useAuth } from '~/services/auth';
import { UserDatabase } from '~/types/user';
import { getAdventurerURL } from '~/config/endpoints.config';

import { useAccess } from './hooks/useAccess';
import { errorMessage } from './utils';
import { AuthShell, AuthField, AuthSubmit, AuthGoogle, AuthSeparator, AuthLink, AuthLegalNotice } from './AuthKit';
import styles from './auth.redesign.module.css';

/**
 * Sign in page.
 */
export function SignIn({ onSignup, onSuccessSignIn }: any) {
    const { t } = useTranslation();
    const { accessSignUp } = useAccess();
    const navigate = useNavigate();

    useEffect(() => {
        logEvent('signin_view', 'SignIn');
        document.title = 'Sign In - Unboared';
    }, []);

    const { loading, login, loginWithGoogle, resetError, emailErrorCode, passwordErrorCode, resetFields } = useAuth();

    const createAccount = () => {
        logEvent('link_signup_click', 'SignIn');
        resetFields();
        if (onSignup) {
            onSignup();
        } else {
            accessSignUp();
        }
    };

    const handleSubmit = (e: any) => {
        logEvent('signin_click', 'SignIn');
        e.preventDefault();
        const formData = new FormData(e.target);
        const email = formData.get('email') as string;
        const password = formData.get('password') as string;
        // `login` renvoie les identifiants en cas de succès, `undefined` en cas
        // d'échec (message d'erreur déjà affiché par le store). La redirection
        // ne passe pas par ici : l'écouteur global d'auth bascule vers les
        // routes connectées (et restaure un éventuel /animator mémorisé).
        login(email, password).then((auth) => {
            if (auth) {
                logEvent('signin_success', 'SignIn');
                onSuccessSignIn?.(auth);
            } else {
                logEvent('signin_failed', 'SignIn');
            }
        });
    };

    const handleSigninGoogle = () => {
        logEvent('google_signin_click', 'SignIn');
        loginWithGoogle().then((auth) => {
            // `loginWithGoogle` renvoie `undefined` en cas d'échec (popup fermée,
            // refus…) : ce n'est pas un succès.
            if (!auth) {
                logEvent('google_signin_failed', 'SignIn');
                return;
            }
            logEvent('google_signin_success', 'SignIn');
            UserDatabase.createUser(
                auth.user.uid,
                auth.user.email || '',
                getAdventurerURL(auth.user.email || ''),
                '' /* firstname */,
                '' /* lastname */
            )
                .then(() => {
                    logEvent('google_signup_success', 'SignUp');
                    onSuccessSignIn?.(auth);
                })
                // Profil rattrapé par `ensureUser` dans le store d'auth (gotcha #170).
                .catch((error) => console.error('[signin] createUser failed', error));
        });
    };

    return (
        <AuthShell>
            <form onSubmit={handleSubmit}>
                <h2 className={styles.title}>{t('global.login.title')}</h2>
                <p className={styles.subtitle}>{t('global.login.subtitle')}</p>

                <AuthGoogle onClick={handleSigninGoogle}>{t('global.login.googleButton')}</AuthGoogle>
                {/* Google crée aussi le compte s'il n'existe pas (gotcha #170) → mention ici */}
                <AuthLegalNotice i18nKey="global.login.legalNotice" />
                <AuthSeparator label={t('global.or')} />

                <AuthField
                    id="email"
                    label={t('global.login.input.email')}
                    placeholder={t('global.login.input.emailPlaceholder')}
                    autoComplete="email"
                    onSelect={resetError}
                    error={emailErrorCode !== undefined}
                    helper={
                        emailErrorCode !== undefined ? t(`global.error.${errorMessage(emailErrorCode)}`) : undefined
                    }
                />
                <AuthField
                    id="password"
                    password
                    label={t('global.login.input.password')}
                    placeholder={t('global.login.input.passwordPlaceholder')}
                    autoComplete="current-password"
                    onSelect={resetError}
                    error={passwordErrorCode !== undefined}
                    helper={
                        passwordErrorCode !== undefined
                            ? t(`global.error.${errorMessage(passwordErrorCode)}`)
                            : undefined
                    }
                />

                <div className={styles.rowEnd}>
                    <AuthLink onClick={() => navigate('/forgotPassword')}>{t('global.login.forgotPassword')}</AuthLink>
                </div>

                <AuthSubmit loading={loading}>{t('global.login.button')}</AuthSubmit>

                <p className={styles.foot}>
                    {t('global.login.noAccount')}{' '}
                    <AuthLink onClick={createAccount}>{t('global.login.createAccount')}</AuthLink>
                </p>
            </form>
        </AuthShell>
    );
}
